import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  type CountryCode,
} from "libphonenumber-js/max";
import { z } from "zod";

const duiValidator = (dui: string) => {
  if (!new RegExp(/\d{9}/i).test(dui)) return false;
  const duiArr = dui.split("").map(Number);
  const validator = duiArr.pop();
  let sum = 0;
  duiArr.forEach((digit, index) => {
    sum += digit * (9 - index);
  });
  const mod = sum % 10;
  if (mod === 0) return validator === 0;
  return validator === 10 - mod;
};

const nitValidator = (nit: string) => {
  if (!new RegExp(/^\d{4}-\d{6}-\d{3}-\d$/i).test(nit)) return false;

  const digitsOnly = nit.replaceAll("-", "");
  const allZeros = digitsOnly.split("").every((digit) => digit === "0");
  if (allZeros) return false;

  return true;
};

const phoneSchema = z
  .object({
    number: z.string().trim().min(1, "El número de teléfono es obligatorio"),
    calling_code: z.string().trim().min(1, "El código de país es obligatorio"),
    country: z.string().trim().min(1, "El país es obligatorio"),
  })
  .superRefine((value, ctx) => {
    if (!value.number) {
      ctx.addIssue({
        code: "custom",
        path: ["number"],
        message: "El número de teléfono es obligatorio",
      });

      return;
    }

    const isValid =
      isValidPhoneNumber(value.number, value.country as CountryCode) &&
      isPossiblePhoneNumber(value.number, value.country as CountryCode);

    if (!isValid) {
      ctx.addIssue({
        code: "custom",
        path: ["number"],
        message: "Formato invalido",
      });
    }
  });

export const createExternalUserSchema = (isPreRegistered = false) =>
  z
    .object({
      type: z.enum(
        ["APPLICANT", "FACILITATOR", "REPRESENTATIVE", "INSTITUTION"],
        {
          error: () => "Debe seleccionar un tipo de usuario",
        },
      ),

      document_type: z.enum(["DUI", "PASSPORT", "CARNE_INSTITUCIONAL", "NIT"], {
        error: () => "Debe seleccionar un tipo de documento",
      }),

      document_number: z
        .string()
        .min(1, "El número de documento es obligatorio")
        .max(
          255,
          "La longitud máxima del número de documento es de 255 caracteres",
        ),

      first_name: z
        .string()
        .min(1, "Este campo es obligatorio")
        .max(255, "La longitud máxima es de 255 caracteres")
        .refine((val) => {
          return val.trim().length > 0;
        }, "Este campo no puede estar vacío"),

      last_name: z
        .string()
        .max(255, "La longitud máxima de los apellidos es de 255 caracteres")
        .optional(),

      commercial_name: z
        .string()
        .max(
          255,
          "La longitud máxima del nombre comercial es de 255 caracteres",
        )
        .optional()
        .or(z.literal("")),

      email: isPreRegistered
        ? z
            .string()
            .max(
              255,
              "La longitud máxima del correo electrónico es de 255 caracteres",
            )
            .optional()
            .or(z.literal(""))
        : z
            .email("El correo electrónico debe ser válido")
            .max(
              255,
              "La longitud máxima del correo electrónico es de 255 caracteres",
            )
            .refine((val) => {
              return val.trim().length > 0;
            }, "El correo electrónico no puede estar vacío"),

      phone: phoneSchema,

      birthdate: z.string().optional().or(z.literal("")),
    })
    .refine(
      (data) => {
        if (data.document_type !== "NIT") {
          if (!data.last_name || data.last_name.trim().length === 0) {
            return false;
          }
        }
        return true;
      },
      {
        path: ["last_name"],
        error: "Los apellidos son obligatorios",
      },
    )
    .refine(
      (data) => {
        // La fecha de nacimiento es obligatoria solo para tipos de documento diferentes a NIT
        if (data.document_type !== "NIT") {
          if (!data.birthdate || data.birthdate.trim().length === 0) {
            return false;
          }
        }
        return true;
      },
      {
        path: ["birthdate"],
        error: "La fecha de nacimiento es obligatoria",
      },
    )
    .refine(
      (data) => {
        // Validar formato solo si la fecha de nacimiento está presente
        if (data.birthdate && data.birthdate.trim().length > 0) {
          const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
          const isoFullRegex =
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/;
          const ddmmyyyyRegex = /^\d{2}\/\d{2}\/\d{4}$/;

          return (
            isoFullRegex.test(data.birthdate) ||
            isoDateRegex.test(data.birthdate) ||
            ddmmyyyyRegex.test(data.birthdate)
          );
        }
        return true;
      },
      {
        path: ["birthdate"],
        error: "Formato de fecha inválido",
      },
    )
    .refine(
      (data) => {
        // Validar que no sea fecha futura solo si la fecha está presente
        if (data.birthdate && data.birthdate.trim().length > 0) {
          let birthDate;

          if (/^\d{2}\/\d{2}\/\d{4}$/.test(data.birthdate)) {
            const [day, month, year] = data.birthdate.split("/");
            birthDate = new Date(`${year}-${month}-${day}`);
          } else {
            birthDate = new Date(data.birthdate);
          }

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          return birthDate <= today;
        }
        return true;
      },
      {
        path: ["birthdate"],
        error: "La fecha de nacimiento no puede ser futura",
      },
    )
    .refine(
      (data) => {
        // Validar edad mínima solo si la fecha está presente
        if (data.birthdate && data.birthdate.trim().length > 0) {
          let birthDate;

          if (/^\d{2}\/\d{2}\/\d{4}$/.test(data.birthdate)) {
            const [day, month, year] = data.birthdate.split("/");
            birthDate = new Date(`${year}-${month}-${day}`);
          } else {
            birthDate = new Date(data.birthdate);
          }

          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();

          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ) {
            age--;
          }

          return age >= 18;
        }
        return true;
      },
      {
        path: ["birthdate"],
        error: "Debe ser mayor de 18 años",
      },
    )
    .refine(
      (data) => {
        if (data.document_type === "DUI") {
          const duiRegex = /^\d{8}-\d$/;
          if (!duiRegex.test(data.document_number)) {
            return false;
          }
          return duiValidator(data.document_number.replace("-", ""));
        }
        if (data.document_type === "NIT") {
          return nitValidator(data.document_number);
        }
        return data.document_number.length > 0;
      },
      {
        path: ["document_number"],
        error: "El número de documento no es válido para el tipo seleccionado",
      },
    )
    .superRefine((data, ctx) => {
      if (data.document_type !== "CARNE_INSTITUCIONAL") return;
      const { length } = data.document_number.trim();
      if (length < 4 || length > 20) {
        ctx.addIssue({
          code: "custom",
          message: "El número de carné debe tener entre 4 y 20 caracteres",
          path: ["document_number"],
        });
      }
    });

export const externalUserSchema = createExternalUserSchema(false);

export type ExternalUserFormData = z.infer<typeof externalUserSchema>;
